import React, { useEffect, useState } from 'react';
import {
  Brain, Loader2, RefreshCw, Plus, Trash2, ToggleLeft, ToggleRight,
  Star, Zap, CheckCircle, AlertCircle, X,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';

const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  performance: { label: '📊 Performance', color: '#3B82F6' },
  prompt: { label: '✏️ Prompt', color: '#8B5CF6' },
  approval: { label: '✅ Freigabe', color: '#10B981' },
  brand: { label: '🎨 Brand', color: '#F59E0B' },
  audience: { label: '👥 Zielgruppe', color: '#EC4899' },
  platform: { label: '📱 Plattform', color: '#06B6D4' },
  campaign: { label: '📢 Kampagne', color: '#EF4444' },
  idea: { label: '💡 Idee', color: '#84CC16' },
  feedback: { label: '💬 Feedback', color: '#6366F1' },
};

export const MarketingLearningPage: React.FC = () => {
  const [learnings, setLearnings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLearning, setNewLearning] = useState({ learningType: 'brand', insight: '' });
  const [aggregating, setAggregating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [lRes, sRes] = await Promise.all([
        adminFetch(`/api/admin/marketing/learnings`, { headers: { } }),
        adminFetch(`/api/admin/marketing/learning-stats`, { headers: { } }),
      ]);
      if (lRes.ok) { const d = await lRes.json(); setLearnings(d.learnings || []); }
      if (sRes.ok) setStats(await sRes.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleToggle = async (id: string, currentActive: boolean) => {
    await adminFetch(`/api/admin/marketing/learnings/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isActive: !currentActive }),
    });
    await loadAll();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Learning wirklich löschen?')) return;
    await adminFetch(`/api/admin/marketing/learnings/${id}`, {
      method: 'DELETE',     });
    await loadAll();
  };

  const handleAdd = async () => {
    if (!newLearning.insight.trim()) return;
    await adminFetch(`/api/admin/marketing/learnings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newLearning),
    });
    setShowAddModal(false);
    setNewLearning({ learningType: 'brand', insight: '' });
    await loadAll();
  };

  const handleAggregate = async () => {
    setAggregating(true);
    try {
      const resp = await adminFetch(`/api/admin/marketing/cron/aggregate-learnings`, {
        method: 'POST',
              });
      const data = await resp.json();
      setMessage(`✅ ${data.signalsProcessed} Signale verarbeitet, ${data.memoriesCreated} neue, ${data.memoriesUpdated} aktualisierte Learnings.`);
      setTimeout(() => setMessage(''), 5000);
      await loadAll();
    } catch (err) { console.error(err); }
    finally { setAggregating(false); }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><Brain size={24} /> Learning Engine</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1><Brain size={24} /> Learning Engine</h1>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={handleAggregate} disabled={aggregating}>
            {aggregating ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
            Aggregation starten
          </button>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <Plus size={14} /> Manuelles Learning
          </button>
          <button className="btn btn-secondary" onClick={loadAll}><RefreshCw size={14} /></button>
        </div>
      </div>

      {message && (
        <div className="card mb-4" style={{ padding: 12, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
          <span style={{ fontSize: 13 }}>{message}</span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid-cols-4 mb-4">
          <div className="kpi-card"><span className="kpi-label">Aktive Learnings</span><span className="kpi-value" style={{ color: '#8B5CF6' }}>{stats.activeLearnings}</span></div>
          <div className="kpi-card"><span className="kpi-label">Gesamt</span><span className="kpi-value">{stats.totalLearnings}</span></div>
          <div className="kpi-card"><span className="kpi-label">Manuelle</span><span className="kpi-value" style={{ color: '#F59E0B' }}>{stats.manualLearnings}</span></div>
          <div className="kpi-card">
            <span className="kpi-label">Unverarbeitete Signale</span>
            <span className="kpi-value" style={{ color: stats.unprocessedSignals > 0 ? '#EF4444' : '#10B981' }}>{stats.unprocessedSignals}</span>
          </div>
        </div>
      )}

      {/* Learnings List */}
      {learnings.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Brain size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>Noch keine Learnings. Die KI lernt mit jedem Post dazu.</p>
          <p className="text-xs mt-2">Du kannst auch manuelle Learnings hinzufügen, z.B.: "Verwende niemals das Wort gratis"</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {learnings.map(l => {
            const typeConf = TYPE_LABELS[l.learning_type] || { label: l.learning_type, color: '#6B7280' };
            return (
              <div key={l.id} className="card" style={{
                padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: 14,
                opacity: l.is_active ? 1 : 0.5,
                borderLeft: `3px solid ${typeConf.color}`,
              }}>
                <div style={{ flex: 1 }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 6, background: `${typeConf.color}15`, color: typeConf.color, fontWeight: 600 }}>
                      {typeConf.label}
                    </span>
                    {l.is_manual && <span title="Manuell"><Star size={12} style={{ color: '#F59E0B' }} /></span>}
                    <span className="text-xs text-muted">
                      {Math.round(l.confidence * 100)}% Confidence • {l.data_points} Datenpunkte
                    </span>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>{l.insight}</div>
                </div>
                <div className="flex gap-1" style={{ flexShrink: 0 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleToggle(l.id, l.is_active)}
                    title={l.is_active ? 'Deaktivieren' : 'Aktivieren'}>
                    {l.is_active ? <ToggleRight size={14} style={{ color: '#10B981' }} /> : <ToggleLeft size={14} />}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(l.id)} title="Löschen">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowAddModal(false)}>
          <div className="card" style={{ padding: 24, width: 450, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 style={{ margin: 0 }}><Star size={18} style={{ color: '#F59E0B' }} /> Manuelles Learning</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddModal(false)}><X size={14} /></button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Typ</label>
              <select className="input" value={newLearning.learningType} onChange={e => setNewLearning(l => ({ ...l, learningType: e.target.value }))} style={{ width: '100%' }}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Erfahrungswert / Anweisung</label>
              <textarea className="input" value={newLearning.insight} onChange={e => setNewLearning(l => ({ ...l, insight: e.target.value }))}
                rows={4} placeholder='z.B. "Posts mit emotionalen Hooks performen 2x besser" oder "Verwende niemals das Wort kostenlos"'
                style={{ width: '100%', resize: 'vertical', fontSize: 13 }} />
            </div>
            <button className="btn btn-primary" onClick={handleAdd} disabled={!newLearning.insight.trim()}>
              <CheckCircle size={14} /> Learning hinzufügen
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
};
